import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Minus, Plus } from "lucide-react";
import { Modal } from "../../UI/Modal";
import Button from "../../UI/Button";
import { WORK_SERVICE_TYPES } from "../../../constants/worksConstants";
import { ServiceType } from "../../../types/works";

interface ServicePickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Services offered — the client's (or General) directory entries; falls back
   *  to the fixed works catalog when none are supplied. */
  serviceTypes?: ServiceType[];
  /** Price + tax are resolved from the chosen directory entry by the parent.
   *  For MATRIX services the picker resolves the price from the selected
   *  zone × band cell and passes it (plus a describing suffix) via `extra`. */
  onAdd: (
    serviceTypeId: string,
    quantity: number,
    note?: string,
    extra?: { unitPrice?: number; nameSuffix?: string },
  ) => void;
}

/** Catalog-driven service add (chips → quantity → subtotal), per the edit-views mockup. */
export const ServicePicker: React.FC<ServicePickerProps> = ({
  isOpen,
  onClose,
  serviceTypes: serviceTypesProp,
  onAdd,
}) => {
  const serviceTypes = serviceTypesProp ?? WORK_SERVICE_TYPES;
  const { t } = useTranslation("works");
  const [serviceTypeId, setServiceTypeId] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);
  const [note, setNote] = useState("");
  // MATRIX services: the driver must pick a zone + band to resolve the price.
  const [zoneId, setZoneId] = useState<string>("");
  const [bandId, setBandId] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      setServiceTypeId(serviceTypes[0]?.id ?? "");
      setQuantity(1);
      setNote("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, serviceTypes]);

  // Reset the zone/band choice whenever the chosen service changes (including
  // the initial selection on open) — a stale zone/band from another service
  // would resolve to the wrong (or no) matrix cell.
  useEffect(() => {
    setZoneId("");
    setBandId("");
  }, [serviceTypeId]);

  const selected = serviceTypes.find((s) => s.id === serviceTypeId);
  const isMatrix = selected?.pricingType === "MATRIX";
  const zones = selected?.zones ?? [];
  const bands = selected?.bands ?? [];
  const selectedZone = zones.find((z) => z._id === zoneId);
  const selectedBand = bands.find((b) => b._id === bandId);

  // MATRIX price is resolved from the chosen zone × band cell; every other type
  // uses the directory entry's fixed price. The driver never types a price.
  const matrixCell =
    isMatrix && zoneId && bandId
      ? selected?.priceMatrix?.find((c) => c.zoneId === zoneId && c.bandId === bandId)
      : undefined;
  const priceNum = isMatrix ? matrixCell?.price ?? 0 : selected?.defaultPrice ?? 0;
  const subtotal = Math.round(priceNum * quantity * 100) / 100;
  /**
   * A resolved price, not a positive one.
   *
   * MATRIX still fails closed: a zone/band pair with no cell in the grid
   * resolves to 0, and that genuinely IS unresolved — there is nothing to
   * charge and nothing the office can look up later. Every other type may
   * legitimately be 0: a free service, or a card with no default rate that the
   * office prices when the work is invoiced (see Service.price). Refusing
   * those made a price-less service impossible to record at all, so the work
   * went unbilled rather than under-billed.
   */
  const priceValid = isMatrix ? priceNum > 0 : priceNum >= 0;
  // For matrix services both axes are mandatory before the line can be added.
  const matrixReady = !isMatrix || (!!zoneId && !!bandId);
  const canAdd = !!serviceTypeId && matrixReady && priceValid;

  const handleAdd = () => {
    if (!canAdd) return;
    const extra = isMatrix
      ? {
          unitPrice: priceNum,
          nameSuffix: `${selectedZone?.name ?? ""} · ${selectedBand?.label ?? ""}`.trim(),
        }
      : undefined;
    onAdd(serviceTypeId, quantity, note.trim() || undefined, extra);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("driver.servicePicker.title")} size="sm">
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:p-6 space-y-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8A9590] mb-1.5">
            {t("driver.servicePicker.chooseService")}
          </div>
          {serviceTypes.length === 0 ? (
            <p className="text-sm text-[#8A9590]">{t("driver.servicePicker.noServices")}</p>
          ) : (
            <select
              value={serviceTypeId}
              onChange={(e) => setServiceTypeId(e.target.value)}
              className="w-full h-10 px-3 text-sm rounded-lg border border-[#E5E3DD] bg-white text-[#2B2F2E] focus:outline-none focus:ring-2 focus:ring-[#C97A3C]/30 focus:border-[#C97A3C]"
            >
              {serviceTypes.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.icon} {service.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Matrix zone + band pickers — both mandatory to resolve the price. */}
        {isMatrix && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8A9590] mb-1.5">
                {t("driver.servicePicker.zone")}
              </div>
              <select
                value={zoneId}
                onChange={(e) => setZoneId(e.target.value)}
                className={`w-full h-10 px-3 text-sm rounded-lg border bg-white text-[#2B2F2E] focus:outline-none focus:ring-2 focus:ring-[#C97A3C]/30 focus:border-[#C97A3C] ${
                  zoneId ? "border-[#E5E3DD]" : "border-[#E24B4A]"
                }`}
              >
                <option value="">{t("driver.servicePicker.selectZone")}</option>
                {zones.map((z) => (
                  <option key={z._id} value={z._id}>
                    {z.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8A9590] mb-1.5">
                {t("driver.servicePicker.band")}
              </div>
              <select
                value={bandId}
                onChange={(e) => setBandId(e.target.value)}
                className={`w-full h-10 px-3 text-sm rounded-lg border bg-white text-[#2B2F2E] focus:outline-none focus:ring-2 focus:ring-[#C97A3C]/30 focus:border-[#C97A3C] ${
                  bandId ? "border-[#E5E3DD]" : "border-[#E24B4A]"
                }`}
              >
                <option value="">{t("driver.servicePicker.selectBand")}</option>
                {bands.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Quantity stepper */}
        <div className="flex items-stretch rounded-xl border-2 border-[#C97A3C] overflow-hidden">
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.max(0.5, Math.round((q - 0.5) * 10) / 10))}
            className="w-16 bg-[#FCEEE2] text-[#C97A3C] flex items-center justify-center hover:bg-[#F8E0CC] transition-colors"
            aria-label={t("driver.metricEdit.decrease")}
          >
            <Minus size={22} />
          </button>
          <div className="flex-1 flex items-center justify-center py-4 text-3xl font-bold text-[#0B0F0E]">
            {Number.isInteger(quantity) ? quantity : quantity.toFixed(1)}
          </div>
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.round((q + 0.5) * 10) / 10)}
            className="w-16 bg-[#FCEEE2] text-[#C97A3C] flex items-center justify-center hover:bg-[#F8E0CC] transition-colors"
            aria-label={t("driver.metricEdit.increase")}
          >
            <Plus size={22} />
          </button>
          <div className="px-4 flex items-center bg-[#FAF9F6] border-l border-[#ECEAE4] text-[#8A9590] font-semibold">
            {selected?.unit}
          </div>
        </div>

        {/* Summary — rate comes from the directory entry, read-only. */}
        <div className="bg-[#FAF9F6] rounded-lg p-3 text-sm space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-[#8A9590]">{t("driver.servicePicker.rate")}</span>
            <span className="font-medium text-[#2B2F2E] tabular-nums">
              €{priceNum}
              {selected?.unit ? ` / ${selected.unit}` : ""}
            </span>
          </div>
          {isMatrix && !matrixReady && (
            <p className="text-[11px] text-[#C9952F] text-right">
              {t("driver.servicePicker.selectZoneBand")}
            </p>
          )}
          {isMatrix && matrixReady && !priceValid && (
            <p className="text-[11px] text-[#E24B4A] text-right">
              {t("driver.servicePicker.noMatrixPrice")}
            </p>
          )}
          {/* Not an error any more — the line can be added. It says the
              rate is not on the card, so the office sets it at invoicing.
              Keyed on `hasRate`, not on the price being zero: a service that
              is deliberately free is priced correctly and has nothing to
              report, and warning about it would teach the driver to ignore
              the one case that does need them. */}
          {!isMatrix && selected?.hasRate === false && (
            <p className="text-[11px] text-[#C9952F] text-right">
              {t("driver.servicePicker.noPrice")}
            </p>
          )}
          <div className="flex justify-between border-t border-[#ECEAE4] pt-1.5">
            <span className="text-[#8A9590] font-medium">{t("driver.servicePicker.subtotal")}</span>
            <span className="font-bold text-[#C97A3C]">€{subtotal}</span>
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#8A9590] mb-1.5">
            {t("driver.servicePicker.notes")}
          </label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("driver.servicePicker.notesPlaceholder")}
            className="w-full px-3 py-2 text-sm rounded-lg border border-[rgba(11,15,14,0.15)] bg-white text-[#2B2F2E] placeholder:text-[#8A9590] focus:outline-none focus:ring-2 focus:ring-[#C97A3C]/30 focus:border-[#C97A3C] resize-none"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 px-4 py-3 sm:px-6 border-t border-[rgba(11,15,14,0.08)] bg-white shrink-0">
        <Button variant="outline" onClick={onClose}>
          {t("form.cancel")}
        </Button>
        <Button
          variant="primary"
          disabled={!canAdd}
          onClick={handleAdd}
          icon={<Plus size={16} />}
        >
          {t("driver.servicePicker.add")}
        </Button>
      </div>
    </Modal>
  );
};
